import each from 'lodash/each'
import get from 'lodash/get'
import map from 'lodash/map'
import $ from 'jquery'
import 'slick-carousel'
import imagesLoaded from 'imagesloaded'
import PhotoSwipe from '@PhotoSwipe'
import PhotoSwipeLightbox from '@PhotoSwipeLightbox'
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin'
import MiniMasonry from 'minimasonry'
import eventBus from '../../eventBus'
import { createStyleManager } from '../../../shared/styleManager'
import nl2br from '../../../shared/nl2br'
import { breakpoints } from '../../breakpoints'
import {
  BP_LARGE, BP_MEDIUM, BP_XLARGE, BP_XXLARGE,
} from '../../../shared/stylesheet/Breakpoints'

export default {
  // language=HTML
  template: `
    <div class="photo-gallery" :id="elId" :class="{
      'photo-gallery--grid': isGrid,
      'photo-gallery--with-captions': isGrid && grid.showCaptions,
      'photo-gallery--masonry-grid': isMasonry,
      'force-square-photos': forceSquarePhotos,
    }">
      <template v-if="isGrid || isMasonry">
        <a
          v-for="(photo, photoIdx) in photos"
          class="photo-gallery__item"
          @click.prevent="openGridPhoto(photo)"
          :href="photoBaseUrl + photo.path"
          :aria-label="getAriaLabel(photo, photoIdx)"
        ><img
          :src="photoBaseUrl + photo.dir + '_thumb/' + photo.filename"
          alt="">
          <template v-if="isGrid && grid.showCaptions">
            <span class="photo-gallery__item-title" v-if="photo.title">{{ photo.title }}</span>
            <span
              class="photo-gallery__item-description"
              v-if="photo.description"
              v-html="nl2br(photo.description)"></span>
          </template>
        </a>
      </template>
      <template v-else-if="isCarousel">
        <div ref="bigSlides" class="big-slides" :class="bigSlidesClassObject">
          <button
            v-for="(photo, photoIdx) in photos"
            :key="photo.id"
            type="button" class="slide"
            :aria-label="'Enlarge photo #' + (photoIdx + 1)"
          >
            <img :data-lazy="photoBaseUrl + photo.dir + photo.filename" alt="" />
          </button>
        </div>
        <div ref="thumbSlides" v-if="photos.length > 1" class="thumb-slides" :class="thumbSlidesClassObject">
          <button
            v-for="(photo, photoIdx) in photos"
            :key="photo.id"
            type="button" class="slide"
            :aria-label="'View photo #' + (photoIdx + 1)"
          >
            <span class="image-window" :style="getThumbSlideStyle(photo)"></span>
          </button>
        </div>
      </template>
      <span v-if="gridCss" v-html="gridCss"></span>
    </div>
  `,
  props: {
    id: {
      type: String,
      required: true,
    },
    // carousel, grid, lightbox, masonry
    mode: {
      type: String,
    },
    carouselSettings: {
      type: Object,
      required: true,
    },
    forceSquarePhotos: {
      type: Boolean,
      required: true,
    },
    masonry: {
      type: Object,
      required: true,
    },
    grid: {
      type: Object,
      required: true,
    },
    photos: {
      type: Array,
      required: true,
      default() {
        return []
      },
    },
    photoBaseUrl: {
      type: String,
      default: window.APP.context.site.files_url,
    },
  },
  computed: {
    computedMode() {
      return this.mode || 'carousel'
    },
    elId() {
      if (this.isLightbox) {
        return `lightbox-${this.id}`
      }
      return this.id
    },
    isGrid() {
      return this.computedMode === 'grid'
    },
    isCarousel() {
      return this.computedMode === 'carousel'
    },
    isLightbox() {
      return this.computedMode === 'lightbox'
    },
    isMasonry() {
      return this.computedMode === 'masonry'
    },
    gridCss() {
      if (!this.isGrid) {
        return ''
      }

      const manager = createStyleManager()
      const gridSelector = `.cms-block-${this.elId} .photo-gallery--grid`

      const maybeAddCss = (prop, val, breakpoint = null) => {
        if (!val) {
          return
        }
        manager.addStyle(gridSelector, prop, val, breakpoint)
      }

      Object.keys(this.grid.gaps).forEach((breakpoint) => {
        maybeAddCss(`--${breakpoint}-gap`, this.grid.gaps[breakpoint])
      })

      Object.keys(this.grid.cols).forEach((breakpoint) => {
        maybeAddCss(`--${breakpoint}-columns`, this.grid.cols[breakpoint])
      })

      return manager.render()
    },
    carouselThumbSlidesToShow() {
      return {
        default: get(this.carouselSettings.thumbs, 'small', 4),
        breakpoints: [
          {
            breakpoint: breakpoints[BP_MEDIUM].size(),
            slides: get(this.carouselSettings.thumbs, 'medium', 5),
          },
          {
            breakpoint: breakpoints[BP_LARGE].size(),
            slides: get(this.carouselSettings.thumbs, 'large', 6),
          },
          {
            breakpoint: breakpoints[BP_XLARGE].size(),
            slides: get(this.carouselSettings.thumbs, 'xlarge', 6),
          },
          {
            breakpoint: breakpoints[BP_XXLARGE].size(),
            slides: get(this.carouselSettings.thumbs, 'xxlarge', 6),
          },
        ],
      }
    },
    bigSlidesClassObject() {
      return {
        [`big-slides-${this.id}`]: true,
      }
    },
    thumbSlidesClassObject() {
      return {
        [`thumb-slides-${this.id}`]: true,
      }
    },
    photoswipePhotos() {
      return map(this.photos, (photo) => ({
        src: this.photoBaseUrl + photo.path,
        w: photo.width,
        h: photo.height,
        title: photo.title,
        description: photo.description,
      }))
    },
    photoswipeOptions() {
      return {
        bgOpacity: 1,
        pswpModule: PhotoSwipe,
        dataSource: this.photoswipePhotos,
        paddingFn: (viewportSize) => ({
          top: viewportSize.y > 768 ? 100 : 0,
          bottom: viewportSize.y > 768 ? 100 : 0,
          left: 0,
          right: 0,
        }),
      }
    },
  },
  methods: {
    getAriaLabel(photo, photoIdx) {
      return get(photo, 'title', `View Photo #${photoIdx + 1}`)
    },
    getThumbSlideStyle(photo) {
      return {
        backgroundImage: `url('${this.photoBaseUrl}${photo.dir}_thumb/${photo.filename}')`,
      }
    },

    openGridPhoto(photo) {
      this.lightbox.loadAndOpen(this.photos.indexOf(photo))
    },

    nl2br(val) {
      return nl2br(val)
    },

    setupCarousel() {
      const bigSlidesSelector = `.big-slides-${this.id}`
      const thumbSlidesSelector = `.thumb-slides-${this.id}`

      const opts = {
        infinite: true,
        lazyLoad: 'progressive',
        adaptiveHeight: true,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        dots: false,
      }

      if (this.$refs.thumbSlides) {
        opts.asNavFor = thumbSlidesSelector
      }

      $(this.$refs.bigSlides).slick(opts)

      $(this.$refs.bigSlides).on('click', '.slide', (ev) => {
        ev.preventDefault()
        this.lightbox.loadAndOpen($(ev.currentTarget).closest('.slick-slide').data('slick-index'))
      })

      if (this.$refs.thumbSlides) {
        const responsive = []
        each(this.carouselThumbSlidesToShow.breakpoints, (breakpoint) => {
          responsive.push({
            breakpoint: breakpoint.breakpoint,
            settings: {
              arrows: breakpoint.breakpoint >= 768,
              centerMode: true,
              centerPadding: '60px',
              slidesToShow: breakpoint.slides,
              slidesToScroll: breakpoint.slides,
            },
          })
        })

        $(this.$refs.thumbSlides).slick({
          asNavFor: bigSlidesSelector,
          focusOnSelect: true,
          infinite: true,
          speed: 300,
          slidesToShow: this.carouselThumbSlidesToShow.default,
          slidesToScroll: this.carouselThumbSlidesToShow.default,
          prevArrow: $('#slider-prev').html(),
          nextArrow: $('#slider-next').html(),
          centerMode: false,
          mobileFirst: true,
          arrows: false, // mobile-only. overridden by responsive settings
          responsive,
        })

        $(this.$refs.thumbSlides).on('click', '.slide', (ev) => {
          $(this.$refs.thumbSlides).slick('slickGoTo', $(ev.currentTarget).closest('.slick-slide').data('slick-index'))
        })
      }
    },

    handleOpenLightbox(galleryId) {
      const actualGalleryId = galleryId.replace(/^#lightbox-/g, '')

      if (actualGalleryId !== this.id || !this.isLightbox) {
        return
      }

      this.lightbox.loadAndOpen(0)
    },

    setupMasonry() {
      if (this.masonryApi) {
        this.masonryApi.destroy()
      }

      if (!this.isMasonry) {
        return
      }

      const img = this.$el.querySelector('img')
      if (img) {
        this.masonryApi = new MiniMasonry({
          container: this.$el,
          gutter: 20,
          baseWidth: (this.masonry.width ? parseInt(this.masonry.width, 10) : false) || img.width,
        })
      }
    },
  },

  created() {
    this.lightbox = new PhotoSwipeLightbox({ ...this.photoswipeOptions })
    new PhotoSwipeDynamicCaption(this.lightbox, {
      type: 'below',
      captionContent: (slide) => {
        const html = []
        if (slide.data.title) {
          html.push(slide.data.title)
        }
        if (slide.data.description) {
          html.push(`<small>${slide.data.description}</small>`)
        }
        return html.join('<br>')
      },
    })
    this.lightbox.init()
    eventBus.$on('open-lightbox', this.handleOpenLightbox)
  },

  mounted() {
    this.$nextTick(() => {
      if (this.isCarousel) {
        this.setupCarousel()
      } else if (this.isMasonry) {
        imagesLoaded(this.$el, () => {
          this.setupMasonry()
        })
      }
    })
  },

  beforeDestroy() {
    eventBus.$off('open-lightbox', this.handleOpenLightbox)
  },
}
